import { useState, useMemo, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import Logo from '@/components/feature/Logo';
import {
  getContractByToken,
  getContractStatusLabel,
  type RadioContract,
  type SignatureEvidence,
} from '@/mocks/superAdminContracts';
import { getPlanColor } from '@/mocks/superAdminPlans';
import { defaultClauses, planSpecificClauses } from '@/mocks/superAdminContracts';

type PageState = 'loading' | 'ready' | 'error' | 'invalid' | 'already_signed' | 'signing' | 'signed';

function simulateLoad(token: string): Promise<RadioContract | undefined> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(getContractByToken(token));
    }, 600);
  });
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('es-AR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const mockUserAgents = [
  'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/132.0.0.0 Safari/537.36',
  'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.3 Safari/605.1.15',
  'Mozilla/5.0 (X11; Linux x86_64; rv:134.0) Gecko/20100101 Firefox/134.0',
  'Mozilla/5.0 (iPhone; CPU iPhone OS 18_3 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/18.3 Mobile/15E148 Safari/604.1',
  'Mozilla/5.0 (Linux; Android 15; Pixel 9 Pro) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/132.0.0.0 Mobile Safari/537.36',
];

const mockIps = [
  '190.210.45.127',
  '181.44.67.203',
  '201.159.88.14',
  '186.23.105.42',
  '200.115.34.89',
];

export default function PublicSignContract() {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [contract, setContract] = useState<RadioContract | null>(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [writtenSignature, setWrittenSignature] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [readAll, setReadAll] = useState(false);

  const [signatureEvidence, setSignatureEvidence] = useState<SignatureEvidence | null>(null);

  useEffect(() => {
    if (!token) {
      setPageState('error');
      setErrorMsg('Token de firma no proporcionado.');
      return;
    }
    simulateLoad(token)
      .then((data) => {
        if (!data) {
          setPageState('invalid');
          setErrorMsg('El link de firma no es válido o el contrato no fue encontrado.');
          return;
        }
        if (!data.signatureLinkValid) {
          setPageState('invalid');
          setErrorMsg('Este link de firma ha sido invalidado por el administrador. Contactá a soporte si necesitás un nuevo link.');
          return;
        }
        if (data.status === 'signed' && data.signatureEvidence) {
          setContract(data);
          setSignatureEvidence(data.signatureEvidence);
          setPageState('already_signed');
          return;
        }
        if (data.status === 'cancelled') {
          setPageState('invalid');
          setErrorMsg('Este contrato ha sido cancelado y no puede ser firmado.');
          return;
        }
        setContract(data);
        setPageState('ready');
      })
      .catch(() => {
        setPageState('error');
        setErrorMsg('Error al cargar el contrato. Verificá tu conexión e intentá de nuevo.');
      });
  }, [token]);

  const allClauses = useMemo(() => {
    if (!contract) return [];
    const defaults = defaultClauses.filter((c) => contract.clauses.includes(c.id));
    const specifics = planSpecificClauses.filter((c) => contract.clauses.includes(c.id));
    return [...defaults, ...specifics];
  }, [contract]);

  const canSign = signerName.trim() && signerEmail.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signerEmail.trim()) && writtenSignature.trim() && acceptedTerms && readAll;

  const handleSign = () => {
    if (!canSign || !contract) return;

    const evidence: SignatureEvidence = {
      signedAt: new Date().toISOString(),
      signerIp: mockIps[Math.floor(Math.random() * mockIps.length)],
      signerUserAgent: mockUserAgents[Math.floor(Math.random() * mockUserAgents.length)],
      signedByName: signerName.trim(),
      signedByEmail: signerEmail.trim(),
      signedByIdentifier: 'DNI/Pasaporte — Registrado al firmar',
      writtenSignature: writtenSignature.trim(),
    };

    setPageState('signing');

    Swal.fire({
      icon: 'question',
      title: '¿Confirmar firma del contrato?',
      html: `
        <div style="text-align:left;font-size:13px">
          <p style="margin-bottom:8px"><strong>${contract.contractNumber}</strong> — ${contract.radioName}</p>
          <p style="margin-bottom:4px">Firmante: <strong>${signerName.trim()}</strong></p>
          <p style="margin-bottom:4px">Email: <strong>${signerEmail.trim()}</strong></p>
          <p style="color:#f97316;font-size:12px;margin-top:8px">La firma electrónica tiene validez legal. Esta acción no se puede deshacer.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Firmar Contrato',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#d1d5db',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        cancelButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap text-gray-700',
      },
      buttonsStyling: false,
    }).then((result) => {
      if (result.isConfirmed) {
        setSignatureEvidence(evidence);
        setPageState('signed');
        Swal.fire({
          icon: 'success',
          title: 'Contrato firmado correctamente.',
          text: `${signerName.trim()} ha firmado el contrato electrónicamente. La radio ya puede ser activada.`,
          confirmButtonColor: '#10b981',
          customClass: {
            popup: 'rounded-2xl text-sm',
            title: 'text-sm font-bold',
            confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
          },
        });
      } else {
        setPageState('ready');
      }
    });
  };

  const handleDownloadPdf = () => {
    Swal.fire({
      icon: 'info',
      title: 'Descarga de PDF',
      text: 'El PDF del contrato se generará próximamente con la integración de documentos.',
      confirmButtonColor: '#1a1a1a',
      customClass: {
        popup: 'rounded-2xl text-sm',
        title: 'text-sm font-bold',
        confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl',
      },
    });
  };

  // Loading
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 mx-auto rounded-full border-2 border-foreground-300 border-t-transparent animate-spin"></div>
          <p className="text-foreground-500 text-sm">Cargando contrato...</p>
        </div>
      </div>
    );
  }

  // Error / Invalid
  if (pageState === 'error' || pageState === 'invalid') {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center">
            <i className="ri-error-warning-line text-red-400 text-2xl"></i>
          </div>
          <h2 className="text-foreground-800 text-lg font-bold mb-2">
            {pageState === 'error' ? 'Error al cargar el contrato' : 'Link no válido'}
          </h2>
          <p className="text-foreground-400 text-sm mb-6">{errorMsg}</p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-line"></i>
            Volver al inicio
          </Link>
        </div>
      </div>
    );
  }

  // Already signed
  if (pageState === 'already_signed' && contract && signatureEvidence) {
    return (
      <SignedSuccessView
        contract={contract}
        evidence={signatureEvidence}
        onDownloadPdf={handleDownloadPdf}
      />
    );
  }

  // Signed just now
  if (pageState === 'signed' && contract && signatureEvidence) {
    return (
      <SignedSuccessView
        contract={contract}
        evidence={signatureEvidence}
        onDownloadPdf={handleDownloadPdf}
      />
    );
  }

  if (!contract) return null;

  return (
    <div className="min-h-screen bg-background-100">
      {/* Top bar */}
      <header className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo size="sm" />
            <div className="hidden sm:block">
              <h1 className="text-foreground-950 font-bold text-sm">RadioFrecuenciaWeb</h1>
              <p className="text-foreground-400 text-[10px]">Portal de Firma Electrónica</p>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="bg-amber-50 border border-amber-200 text-amber-700 text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
              Pendiente de Firma
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10 space-y-6 pb-24">
        {/* Contract Info Card */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-foreground-950 font-bold text-lg">{contract.radioName}</h2>
              <p className="text-foreground-500 text-sm font-mono mt-0.5">{contract.frequency}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-foreground-600 text-[11px] font-medium whitespace-nowrap">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getPlanColor(contract.planId) }}></span>
                Plan {contract.planName}
              </span>
              <span className="px-2.5 py-1 rounded-full bg-background-100 border border-background-200 text-foreground-600 text-[11px] font-medium whitespace-nowrap">
                Lic. Anual ${contract.annualLicense}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Contrato N°</p>
              <p className="text-foreground-800 text-xs font-mono font-bold mt-0.5">{contract.contractNumber}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Plan</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{contract.planName}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Storage</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{contract.storageIncluded}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Cuota Mensual</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">
                {contract.monthlyFee === 0 ? 'Gratis' : `$${contract.monthlyFee}`}
              </p>
            </div>
          </div>

          <button
            onClick={handleDownloadPdf}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line text-sm"></i>
            Descargar PDF
          </button>
        </div>

        {/* Full Contract */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <h3 className="text-foreground-800 font-bold text-sm mb-4 flex items-center gap-2">
            <i className="ri-scales-3-line text-foreground-500"></i>
            Contrato Completo · Versión {contract.contractVersion}
          </h3>

          <div className="space-y-5 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
            {allClauses.map((clause) => (
              <div key={clause.id} className="p-4 bg-background-100 rounded-xl border border-background-200">
                <h4 className="text-foreground-800 text-xs font-bold mb-2">{clause.title}</h4>
                <p className="text-foreground-500 text-[11px] leading-relaxed whitespace-pre-line">{clause.content}</p>
              </div>
            ))}
          </div>

          {/* Special clauses banner */}
          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
            <div className="flex items-start gap-3">
              <i className="ri-scales-3-line text-amber-500 text-lg mt-0.5 shrink-0"></i>
              <div>
                <p className="text-amber-800 text-xs font-semibold mb-1">Documento Legal Vinculante — Información Importante</p>
                <p className="text-amber-700/80 text-[10px] leading-relaxed">
                  Este contrato incluye cláusulas sobre derechos musicales, licencia anual de frecuencia RFW,
                  suspensión por falta de pago, y políticas de contenido. La firma electrónica tiene plena validez legal.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 pt-2 border-t border-amber-200">
              <i className="ri-server-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
              <div>
                <p className="text-amber-800 text-[11px] font-semibold mb-1">RadioFrecuenciaWeb NO vende streaming de radio</p>
                <p className="text-amber-700/80 text-[10px] leading-relaxed mb-1.5">
                  <strong>RFW ofrece:</strong> administración de radio online, perfil público, frecuencia RFW, cabina, chat, comunidad, eventos, fotos, videos, lives dentro de la plataforma si el plan lo permite, retransmisión futura a redes sociales, directorio, app futura.
                </p>
                <p className="text-amber-700/80 text-[10px] leading-relaxed mb-1.5">
                  <strong>RFW NO ofrece:</strong> servidor de streaming de audio, SHOUTcast, Icecast, AutoDJ, ancho de banda de señal de radio, licencias musicales.
                </p>
                <p className="text-amber-700/80 text-[10px] leading-relaxed">
                  Cada radio es responsable de contratar y mantener su propio proveedor de streaming. La radio proporciona su Stream URL para que RFW pueda reproducirla dentro de la plataforma. Si la URL falla, se cae, no transmite o el proveedor externo tiene problemas, RadioFrecuenciaWeb no es responsable por esa interrupción.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 pt-2 border-t border-amber-200">
              <i className="ri-music-2-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
              <div>
                <p className="text-amber-800 text-[11px] font-semibold mb-1">Derechos Musicales y de Autor</p>
                <p className="text-amber-700/80 text-[10px] leading-relaxed">
                  Cada radio es responsable de permisos, licencias, regalías, música, podcasts, programas, retransmisiones y contenido protegido. Esto incluye obligaciones ante SADAIC, ASCAP, BMI, SESAC, AGADU, SAYCO, APDAYC y demás entidades de gestión colectiva. RadioFrecuenciaWeb puede suspender cuentas por reclamos válidos, abuso o incumplimiento.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Signature Form */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <h3 className="text-foreground-800 font-bold text-sm mb-5 flex items-center gap-2">
            <i className="ri-quill-pen-line text-foreground-500"></i>
            Firma Electrónica del Contrato
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1">
                Nombre del Representante Legal <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Nombre completo del representante"
                className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1">
                Email del Firmante <span className="text-red-400">*</span>
              </label>
              <input
                type="email"
                value={signerEmail}
                onChange={(e) => setSignerEmail(e.target.value)}
                placeholder="correo@radio.com"
                className="w-full px-3.5 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors"
              />
            </div>

            <div>
              <label className="block text-foreground-500 text-[10px] font-semibold uppercase tracking-wider mb-1">
                Firma Escrita (Escribí tu nombre completo) <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={writtenSignature}
                onChange={(e) => setWrittenSignature(e.target.value)}
                placeholder="Escribí tu nombre y apellido como firma manuscrita"
                className="w-full px-3.5 py-3 bg-background-50 border-2 border-dashed border-background-300 rounded-xl outline-none focus:border-foreground-400 transition-colors"
                style={{ fontFamily: "'Caveat', cursive", fontSize: '24px' }}
              />
              <p className="text-foreground-300 text-[9px] mt-1">
                Esta firma manuscrita digital tiene validez legal como manifestación de consentimiento.
              </p>
            </div>

            {/* Checkboxes */}
            <div className="space-y-3 pt-2">
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-background-100 transition-colors border border-background-200">
                <input
                  type="checkbox"
                  checked={readAll}
                  onChange={(e) => setReadAll(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-background-300 accent-foreground-950 cursor-pointer"
                />
                <div>
                  <p className="text-foreground-800 text-[11px] font-semibold">He leído y comprendido todas las cláusulas del contrato</p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">
                    Declaro haber leído y comprendido las {allClauses.length} cláusulas del presente Contrato.
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer p-3 rounded-xl hover:bg-background-100 transition-colors border border-background-200">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-background-300 accent-foreground-950 cursor-pointer"
                />
                <div>
                  <p className="text-foreground-800 text-[11px] font-semibold">Acepto todos los términos y condiciones del contrato</p>
                  <p className="text-foreground-400 text-[10px] mt-0.5">
                    La firma electrónica tiene plena validez legal y fuerza ejecutiva según la legislación aplicable.
                  </p>
                </div>
              </label>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-2">
              <i className="ri-scales-3-line text-amber-500 text-sm mt-0.5 shrink-0"></i>
              <p className="text-amber-700 text-[10px] leading-relaxed">
                Al firmar este contrato, La Radio acepta todas las cláusulas aquí contenidas, incluyendo limitaciones de responsabilidad, dependencia de terceros, condición "AS IS" del servicio, y todas las obligaciones legales descritas en el documento.
              </p>
            </div>

            <button
              onClick={handleSign}
              disabled={!canSign}
              className={`w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold transition-all cursor-pointer whitespace-nowrap ${
                canSign
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                  : 'bg-background-200 text-foreground-300 cursor-not-allowed'
              }`}
            >
              <i className="ri-quill-pen-line text-base"></i>
              Firmar Contrato
            </button>

            {!canSign && (
              <div className="text-center text-foreground-300 text-[10px] space-y-0.5">
                {!signerName.trim() && <p>· Ingresá el nombre del representante legal</p>}
                {(!signerEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signerEmail.trim())) && <p>· Ingresá un email válido</p>}
                {!writtenSignature.trim() && <p>· Escribí tu firma manuscrita</p>}
                {!readAll && <p>· Confirmá que leíste todas las cláusulas</p>}
                {!acceptedTerms && <p>· Aceptá los términos y condiciones</p>}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-background-200 bg-background-50 py-4">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-foreground-300 text-[10px]">
            Portal de Firma Electrónica · RadioFrecuenciaWeb · La firma electrónica tiene validez legal
          </p>
        </div>
      </footer>
    </div>
  );
}

/* Signed success view */
function SignedSuccessView({
  contract,
  evidence,
  onDownloadPdf,
}: {
  contract: RadioContract;
  evidence: SignatureEvidence;
  onDownloadPdf: () => void;
}) {
  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-background-50 border-b border-background-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo size="sm" />
            <div className="hidden sm:block">
              <h1 className="text-foreground-950 font-bold text-sm">RadioFrecuenciaWeb</h1>
              <p className="text-foreground-400 text-[10px]">Portal de Firma Electrónica</p>
            </div>
          </Link>
          <span className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold px-2.5 py-1 rounded-full whitespace-nowrap">
            Contrato Firmado
          </span>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-10 space-y-6 pb-24">
        {/* Success banner */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 md:p-6">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <i className="ri-check-double-line text-emerald-500 text-xl"></i>
            </div>
            <div>
              <h2 className="text-emerald-800 text-lg font-bold">Contrato Firmado Correctamente</h2>
              <p className="text-emerald-600 text-sm">{contract.contractNumber} · {contract.radioName}</p>
            </div>
          </div>
          <p className="text-emerald-700/80 text-xs leading-relaxed">
            El contrato ha sido firmado electrónicamente con plena validez legal. La radio ya puede ser activada en la plataforma.
            Conservá una copia del contrato para tus registros.
          </p>
        </div>

        {/* Contract info */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6 space-y-4">
          <h3 className="text-foreground-800 font-bold text-sm flex items-center gap-2">
            <i className="ri-file-text-line text-foreground-500"></i>
            Datos del Contrato
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Contrato</p>
              <p className="text-foreground-800 text-xs font-mono font-bold mt-0.5">{contract.contractNumber}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Radio</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{contract.radioName}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Frecuencia</p>
              <p className="text-foreground-800 text-xs font-mono font-bold mt-0.5">{contract.frequency}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Plan</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{contract.planName}</p>
            </div>
          </div>

          <button
            onClick={onDownloadPdf}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-background-100 border border-background-200 text-foreground-600 text-xs font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-download-line text-sm"></i>
            Descargar Contrato Firmado (PDF)
          </button>
        </div>

        {/* Signature Evidence */}
        <div className="bg-background-50 border border-background-200 rounded-2xl p-5 md:p-6">
          <h3 className="text-foreground-800 font-bold text-sm mb-4 flex items-center gap-2">
            <i className="ri-shield-check-line text-foreground-500"></i>
            Evidencia de Firma Electrónica
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-background-100 rounded-xl p-3.5">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Fecha y Hora</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{formatDateTime(evidence.signedAt)}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3.5">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Firmante</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{evidence.signedByName}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3.5">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Email del Firmante</p>
              <p className="text-foreground-800 text-xs font-mono mt-0.5 truncate">{evidence.signedByEmail}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3.5">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Documento</p>
              <p className="text-foreground-800 text-xs font-bold mt-0.5">{evidence.signedByIdentifier}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3.5">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Dirección IP</p>
              <p className="text-foreground-800 text-xs font-mono font-bold mt-0.5">{evidence.signerIp}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3.5 sm:col-span-2">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">User Agent</p>
              <p className="text-foreground-500 text-[10px] font-mono mt-0.5 break-all">{evidence.signerUserAgent}</p>
            </div>
            <div className="bg-background-100 rounded-xl p-3.5 sm:col-span-2">
              <p className="text-foreground-400 text-[10px] uppercase tracking-wider font-semibold">Firma Manuscrita Digital</p>
              <p className="text-foreground-800 text-xl mt-0.5" style={{ fontFamily: "'Caveat', cursive" }}>
                {evidence.writtenSignature}
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-background-200 bg-background-50 py-4">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <p className="text-foreground-300 text-[10px]">
            Portal de Firma Electrónica · RadioFrecuenciaWeb · Contrato firmado electrónicamente
          </p>
        </div>
      </footer>
    </div>
  );
}